import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Selectable } from '../../../common/context/reducers/selectorReducer'

export type MedidaNameMoto = 'ancho' | 'alto' | 'llanta'

export const OrderedMedidasMoto = [
  'ancho',
  'alto',
  'llanta',
] as MedidaNameMoto[]

export interface SearchNeumaticosMedidasMotoStateType {
  selectedMedidas: { [k in MedidaNameMoto]?: string | number | null }
  previousSelectedMedidas: { [k in MedidaNameMoto]?: string | number | null }
  medidasData: {
    [k in MedidaNameMoto]?: Selectable<number | string>
  }
  isMedidasConfirmed: boolean
}

export const initialState: SearchNeumaticosMedidasMotoStateType = {
  selectedMedidas: {
    ancho: null,
    alto: null,
    llanta: null,
  },
  previousSelectedMedidas: {
    ancho: null,
    alto: null,
    llanta: null,
  },
  medidasData: {
    ancho: { loading: false, value: null, default: null, options: [] },
    alto: { loading: false, value: null, default: null, options: [] },
    llanta: { loading: false, value: null, default: null, options: [] },
  },
  isMedidasConfirmed: false,
}

export const key = 'searchNeumaticosMoto/Medidas'

const searchNeumaticosMedidasMotoSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    setPreviousSelectedMedidasSnapshot: (state) => {
      state.previousSelectedMedidas = { ...state.selectedMedidas }
    },

    updateMedidaSeleccionada: (
      state,
      action: PayloadAction<{
        name: MedidaNameMoto
        value: string | number | null
      }>
    ) => {
      const { name, value } = action.payload

      const updated = { ...state.selectedMedidas }
      updated[name] = value

      const index = OrderedMedidasMoto.indexOf(name)

      // Limpia siguientes medidas seleccionadas.
      for (let i = index + 1; i < OrderedMedidasMoto.length; i++) {
        updated[OrderedMedidasMoto[i]] = null
      }

      state.selectedMedidas = updated

      // Actualiza valor de la medida actual en el store.
      if (state.medidasData[name]) {
        state.medidasData[name] = {
          ...state.medidasData[name],
          value,
        }
      }

      // Limpia siguientes medidas en "medidasData" (options + value).
      for (let i = index + 1; i < OrderedMedidasMoto.length; i++) {
        const nextName = OrderedMedidasMoto[i]
        if (!state.medidasData[nextName]) continue

        state.medidasData[nextName] = {
          ...state.medidasData[nextName],
          value: null,
          options: [],
          loading: true,
        }
      }

      // Confirmem automàticament quan les 3 mides estan completes.
      // Això permet que en seleccionar l'última (llanta) es llanci la cerca.
      const hasAllMedidas = OrderedMedidasMoto.every((k) => {
        const v = updated[k]
        return v != null && v !== ''
      })
      state.isMedidasConfirmed = hasAllMedidas
    },

    setMedidasIniciales: (
      state,
      action: PayloadAction<{
        [k in MedidaNameMoto]?: string | number | null
      }>
    ) => {
      state.selectedMedidas = { ...action.payload }
      state.previousSelectedMedidas = { ...action.payload }
      state.isMedidasConfirmed = true

      for (const name of OrderedMedidasMoto) {
        const selected = action.payload[name]
        const oldOptions = state.medidasData[name]?.options ?? []

        state.medidasData[name] = {
          ...state.medidasData[name],
          value: selected ?? null,
          options: oldOptions,
        }
      }
    },

    setOpcionesMedidas: (
      state,
      action: PayloadAction<
        Partial<SearchNeumaticosMedidasMotoStateType['medidasData']>
      >
    ) => {
      for (const keyName of Object.keys(action.payload) as MedidaNameMoto[]) {
        if (!state.medidasData[keyName]) continue
        state.medidasData[keyName] = {
          ...state.medidasData[keyName],
          ...(action.payload[keyName] as Selectable<number | string>),
        }
      }
    },

    setMedidaLoading: (
      state,
      action: PayloadAction<{ name: MedidaNameMoto; loading: boolean }>
    ) => {
      const { name, loading } = action.payload
      if (!state.medidasData[name]) return
      state.medidasData[name] = {
        ...state.medidasData[name],
        loading,
      }
    },

    restorePreviousMedidas: (state) => {
      state.selectedMedidas = { ...state.previousSelectedMedidas }

      for (const name of OrderedMedidasMoto) {
        const prevVal = state.previousSelectedMedidas[name]
        if (!state.medidasData[name]) continue
        state.medidasData[name] = {
          ...state.medidasData[name],
          value: prevVal ?? null,
        }
      }
    },

    resetMedidasMoto: (state) => {
      state.selectedMedidas = { ...initialState.selectedMedidas }
      state.previousSelectedMedidas = {
        ...initialState.previousSelectedMedidas,
      }
      state.medidasData = {
        ...initialState.medidasData,
      }
      state.isMedidasConfirmed = false
    },

    setMedidasConfirmed: (state, action: PayloadAction<boolean>) => {
      state.isMedidasConfirmed = action.payload
    },
  },
})

export const {
  setPreviousSelectedMedidasSnapshot,
  updateMedidaSeleccionada,
  setMedidasIniciales,
  setOpcionesMedidas,
  setMedidaLoading,
  restorePreviousMedidas,
  resetMedidasMoto,
  setMedidasConfirmed,
} = searchNeumaticosMedidasMotoSlice.actions

export default searchNeumaticosMedidasMotoSlice.reducer
